import React from 'react';
import { func, object, string, bool } from 'prop-types';
import { Text, Stack, Shelf, Icon, Button } from '@chaser/components';
import ReadIndicator from './ReadIndicator/index';
import usePush from '../../../hooks/usePush';
import NotificationService from '../../../services/notification-service';
import style from './NotificationCard.module.scss';
import { formatTimeFromNow } from '../../../util/time';
import LoadingMessage from '../../LoadingMessage/index';

const NotificationCard = ({
  notification,
  shortCode,
  refetch,
  applyOptimisticUpdate,
  isLoading,
  onClick,
  title,
  subtitle,
}) => {
  const { seen, _id, created_at: createdAt } = notification;
  const { execute: executeDelete } = usePush(async (...args) => {
    await NotificationService.deleteNotification(...args);
    await refetch();
  });

  async function deleteNotification(id, sCode, e) {
    e.stopPropagation();
    await applyOptimisticUpdate(state => {
      state.notifications = state.notifications.filter(notif => notif._id !== id);
    }, executeDelete(id, sCode));
  }

  const { execute: executeUpdate } = usePush(async (id, isRead) => {
    if (!isRead) await NotificationService.markNotificationAsUnseen(id, shortCode);
    if (isRead) await NotificationService.markNotificationAsSeen(id, shortCode);

    await refetch();
  });

  async function updateRead(id, isRead, e) {
    e.stopPropagation();
    await applyOptimisticUpdate(state => {
      const foundNotif = state.notifications.find(notif => notif._id === id);
      foundNotif.seen = isRead;
    }, executeUpdate(id, isRead));
  }

  return (
    <div
      className={style.notificationCard}
      role="listitem"
      onClick={e => {
        if (onClick) onClick();
        updateRead(_id, true, e);
      }}
    >
      {isLoading && <LoadingMessage overlay />}
      <Shelf flexWrap="nowrap" gap="xsmall">
        <Stack gap="xsmall" mr="auto">
          <Text fontWeight={!seen ? 'bold' : 'regular'}>{title}</Text>
          <Text>{subtitle}</Text>
          <Text fontSize="xxsmall" color="Mid Neutral 1">
            {formatTimeFromNow(createdAt)}
          </Text>
        </Stack>
        <div className={style.close}>
          <Button
            as="span"
            appearance="minimal"
            size="micro"
            intent="muted"
            onClick={e => deleteNotification(_id, shortCode, e)}
            icon={<Icon name="close" size={12} />}
          />
        </div>
        <ReadIndicator updateRead={updateRead} seen={seen} notifId={_id} />
      </Shelf>
    </div>
  );
};

export default NotificationCard;

NotificationCard.propTypes = {
  shortCode: string.isRequired,
  notification: object.isRequired,
  applyOptimisticUpdate: func.isRequired,
  refetch: func.isRequired,
  onClick: func.isRequired,
  title: string.isRequired,
  subtitle: string.isRequired,
  isLoading: bool,
};
